import { Link, useLocation } from "wouter";
import {
  ArrowRight, Check, FileText, MessageCircle, Plus, Scale, Store, Truck,
  TrendingUp, Users2, Wallet, CalendarClock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { CardCotacaoDaPraca } from "@/components/mercado/cotacao-da-praca";
import { StatusChip } from "@/components/mercado/status-chip";
import { useAuth } from "@/lib/auth-context";
import { usePode } from "@/lib/permissoes";
import { origemAtual } from "@/lib/navegacao";
import { useInicioComprador } from "@/hooks/use-cargas";
import { useConferirComprador } from "@/hooks/use-conferencia";
import { useToast } from "@/hooks/use-toast";
import { fmtKgBR, fmtMoedaBR, fmtToneladas, fmtToneladasSemUnidade } from "@/lib/formato-mercado";
import { diaCurto, CAPACIDADE_REFERENCIA_KG } from "@shared/mercado-inicio";
import type {
  CargaDoPainelDTO, CotacaoPainelDTO, KpiAPagar, KpiComprado, KpiProdutores,
  KpiRomaneios, PainelInicioDTO, PendenciaPainelDTO, ProdutorParceiro,
  RomaneioRecente, SemanaCompra,
} from "@shared/mercado-inicio";
import { cn } from "@/lib/utils";

/**
 * O PAINEL do comprador — Início v2.
 *
 * ⚠️ UMA consulta para nove blocos. Nove `useQuery` seria nove esqueletos
 * piscando em sequência no celular, e nove chances de a tela mostrar números de
 * instantes diferentes sobre a mesma compra.
 *
 * ⚠️ NENHUMA CONTA AQUI. Toneladas, caminhões, percentuais de mistura, médias e
 * deltas chegam prontos do servidor; esta tela só formata. A única aritmética
 * permitida é de LAYOUT — largura de barra em percentual do maior valor, que é
 * geometria, não dinheiro.
 *
 * ⚠️ "Primeiro acesso" não é flag nem tela: é este mesmo painel com dados
 * escassos. Cada bloco tem o seu vazio útil, e a boas-vindas aparece porque há
 * passo pendente.
 */

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
const DIAS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

function saudacao(): string {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function hojePorExtenso(): string {
  const d = new Date();
  return `${DIAS[d.getDay()]}, ${d.getDate()} de ${MESES[d.getMonth()]}`;
}

function mesAtual(): string {
  return MESES[new Date().getMonth()];
}

export default function CompradorInicio() {
  const { user } = useAuth();
  const ehComprador = user?.orgTipo === "COMPRADOR";
  const consulta = useInicioComprador(ehComprador);
  const { data } = consulta;
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });

  if (aguardando || !data) {
    return <CompradorShell className="space-y-4">{aguardando}</CompradorShell>;
  }

  const primeiroNome = (user?.name ?? "").trim().split(/\s+/)[0] || "";
  const novo = !data.onboarding.completo && data.onboarding.feitos <= 2;
  const aConferir = data.kpis.romaneios.aConferir;

  return (
    <CompradorShell className="space-y-5">
      <Cabecalho
        titulo={novo ? `Bem-vindo ao Rizoma, ${primeiroNome}` : `${saudacao()}, ${primeiroNome}`}
        praca={data.cotacao.praca?.nome ?? null}
        aConferir={aConferir}
      />

      {!data.onboarding.completo && <BoasVindas dados={data.onboarding} />}

      <Kpis kpis={data.kpis} />

      {/* Duas colunas INDEPENDENTES, nunca grade 2×2: com grade, a coluna curta
          herda a altura da longa e sobra vão no meio do painel. */}
      <div className="grid gap-4 lg:grid-cols-[1.35fr_1fr] lg:items-start">
        <div className="space-y-4 min-w-0">
          <Cotacao dados={data.cotacao} />
          <Cargas dados={data.cargas} />
          <Romaneios dados={data.romaneios} />
        </div>
        <div className="space-y-4 min-w-0">
          <Pendencias dados={data.pendencias} />
          <ComprasPorSemana semanas={data.comprasPorSemana} />
          <Produtores dados={data.produtores} />
        </div>
      </div>
    </CompradorShell>
  );
}

// ─── §4.1 Cabeçalho ──────────────────────────────────────────────────────────

function Cabecalho({ titulo, praca, aConferir }: {
  titulo: string;
  praca: string | null;
  aConferir: number;
}) {
  // ⚠️ Quem só tem `ver` não vê as ações — a mesma regra das pendências, e agora
  // ela pesa mais: "Novo romaneio" CRIA uma carga, e um botão que responde 403
  // ensina que o app está quebrado.
  const podeOperar = usePode("mercado.corretor.operar");
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight truncate" data-testid="titulo-painel">{titulo}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {hojePorExtenso()}
          {praca && <> · praça {praca}</>}
          {aConferir > 0 && <> · {aConferir} {aConferir === 1 ? "romaneio aguardando conferência" : "romaneios aguardando conferência"}</>}
        </p>
      </div>
      {/* No celular as ações somem daqui: "Novo romaneio" vira o botão
          flutuante acima da tabbar, onde o polegar alcança. */}
      {/* ⚠️ `podeOperar` é do #237 e continua valendo: "Novo romaneio" ESCREVE,
          e um botão que responde 403 ensina que o app está quebrado.

          O que MUDOU é o destino. O #237 fez este botão criar a carga de hoje (ou
          perguntar em qual) porque registrar exigia uma carga — e a própria nota
          dele dizia que era provisório até a jornada de Romaneios. Com a 073 o
          romaneio existe sozinho: o botão vai direto ao formulário, e a carga
          vira a decisão de depois, que é a ordem real do dia. */}
      {podeOperar && (
        <div className="hidden md:flex gap-2">
          {/* "Nova carga" continua indo à lista: lá é que estão os campos da carga. */}
          <Button variant="outline" asChild>
            <Link href="/mercado/cargas" state={origemAtual()}><Truck className="h-4 w-4 mr-1.5" />Nova carga</Link>
          </Button>
          <Button asChild data-testid="button-novo-romaneio">
            <Link href="/mercado/romaneios/novo" state={origemAtual()}>
              <Plus className="h-4 w-4 mr-1.5" />Novo romaneio
            </Link>
          </Button>
        </div>
      )}
      {podeOperar && (
        <Link
          href="/mercado/romaneios/novo"
          state={origemAtual()}
          className="md:hidden fixed right-4 bottom-20 z-30 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-lg"
          data-testid="fab-novo-romaneio"
        >
          <Plus className="h-4 w-4" />Novo romaneio
        </Link>
      )}
    </header>
  );
}

// ─── §4.2 Boas-vindas ────────────────────────────────────────────────────────

function BoasVindas({ dados }: { dados: PainelInicioDTO["onboarding"] }) {
  const pct = Math.round((dados.feitos / dados.total) * 100);
  return (
    <section
      className="rounded-xl bg-[hsl(var(--primary))] text-primary-foreground p-5 md:p-6 grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] md:items-center"
      data-testid="bloco-boas-vindas"
    >
      <div className="min-w-0">
        <h2 className="text-xl font-bold tracking-tight">Seus romaneios e cargas em um só lugar.</h2>
        <p className="mt-1.5 text-sm opacity-90 max-w-[44ch]">
          {/* "enviado por X", nunca "pelo X": o nome pode ser de pessoa ou de
              fazenda, e o artigo certo para os dois não existe. */}
          {dados.primeiroProdutor && <>O romaneio enviado por {dados.primeiroProdutor} já está disponível. </>}
          Registre também os romaneios dos demais produtores para acompanhar, em um só painel, o volume
          comprado, os pagamentos a vencer e a formação das suas cargas.
        </p>
        <div className="mt-4 flex items-center gap-2.5 text-xs opacity-90">
          <div className="h-1.5 w-full max-w-[180px] rounded-full bg-white/20 overflow-hidden">
            <div className="h-full rounded-full bg-[hsl(var(--chart-1))]" style={{ width: `${pct}%` }} />
          </div>
          <span className="whitespace-nowrap">{dados.feitos} de {dados.total} passos</span>
        </div>
      </div>
      <ul className="grid gap-1.5">
        {dados.passos.map(p => (
          <li
            key={p.chave}
            className={cn(
              "grid grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-lg px-3 py-2 text-sm",
              p.feito ? "opacity-80" : "bg-white/10 border border-white/15",
            )}
            data-testid={`passo-${p.chave}`}
          >
            <span className={cn(
              "h-5 w-5 rounded-full grid place-items-center border",
              p.feito ? "bg-[hsl(var(--chart-1))] border-transparent text-primary" : "border-white/40",
            )}>
              {p.feito && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            {/* ⚠️ SEM `truncate`. Em 390px o passo virava "Registrar um romanei…"
                e o subtítulo "agrupe os romaneios de um me…" — o texto que
                explica o que fazer, cortado justamente em quem está começando.
                Quebra em duas linhas: o cartão cresce, a frase fica. */}
            <span className="min-w-0">
              <span className="block">{p.rotulo}</span>
              <span className="block text-[11.5px] opacity-80">{p.sub}</span>
            </span>
            {p.acao && (
              <Button size="sm" variant="secondary" className="h-8" asChild>
                <Link href={p.acao.href} state={origemAtual()}>{p.acao.rotulo}</Link>
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── §4.9 KPIs ───────────────────────────────────────────────────────────────

function Tile({ rotulo, acao, children, className }: {
  rotulo: string;
  acao?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    // ⚠️ `p-3` no celular: com `p-4` sobravam 127px de conteúdo num tile de
    // 159px, e "R$ 13.644,40" (146px) quebrava no meio do número.
    <section className={cn("rounded-lg border bg-card p-3 sm:p-4 flex flex-col gap-1.5 min-h-[112px] sm:min-h-[142px]", className)}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center justify-between gap-2">
        {rotulo}{acao}
      </p>
      {children}
    </section>
  );
}

function Sparkline({ valores }: { valores: number[] }) {
  // Menos de 3 semanas com valor não vira gráfico: três pontos são o mínimo
  // para uma linha dizer alguma coisa; com dois ela sempre parece uma tendência.
  const comValor = valores.filter(v => v > 0).length;
  if (comValor < 3) return null;
  const max = Math.max(...valores, 1);
  const min = Math.min(...valores);
  const pontos = valores.map((v, i) => {
    const x = 3 + (i * 94) / (valores.length - 1);
    const y = 27 - ((v - min) / ((max - min) || 1)) * 22;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const d = `M${pontos.join("L")}`;
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-[30px] mt-auto" aria-hidden="true">
      <path d={`${d}L97,30L3,30Z`} fill="hsl(var(--chart-1))" opacity={0.12} />
      <path d={d} fill="none" stroke="hsl(var(--chart-1))" strokeWidth={1.8} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Kpis({ kpis }: { kpis: PainelInicioDTO["kpis"] }) {
  return (
    // ⚠️ CINCO COLUNAS, não quatro: "A pagar" ocupa duas. Dinheiro é o número
    // mais longo do painel e em 1/4 da linha (181px de conteúdo) "R$ 113.644,40"
    // já não cabia — encolher a fonte só empurraria o problema para o próximo
    // dígito. Com 5 colunas os quatro tiles continuam numa linha só.
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kpis-painel">
      <TileRomaneios dados={kpis.romaneios} />
      <TileComprado dados={kpis.comprado} />
      <TileAPagar dados={kpis.aPagar} />
      <TileProdutores dados={kpis.produtores} />
    </div>
  );
}

function TileRomaneios({ dados }: { dados: KpiRomaneios }) {
  const delta = dados.deltaMesmoPeriodoMesAnterior;
  return (
    <Tile
      rotulo={`Romaneios · ${mesAtual()}`}
      acao={delta != null && (
        <StatusChip kind={delta > 0 ? "pago" : delta < 0 ? "danger" : "aberta"}>
          {delta > 0 ? "+" : ""}{delta} vs mês ant.
        </StatusChip>
      )}
    >
      <p className="font-mono text-[clamp(18px,5.4vw,27px)] font-semibold tabular-nums leading-tight whitespace-nowrap" data-testid="kpi-romaneios">
        {dados.mes}<span className="ml-1.5 text-[13px] font-medium text-muted-foreground font-sans">
          {dados.mes === 1 ? "romaneio" : "romaneios"}
        </span>
      </p>
      <p className="text-xs text-muted-foreground">
        {dados.semana} nesta semana{dados.aConferir > 0 && <> · {dados.aConferir} a conferir</>}
      </p>
      {/* Sem histórico o rodapé vira o convite — o tile nunca fica com um vão. */}
      <div className="mt-auto hidden lg:block">
        {dados.porSemana.filter(v => v > 0).length >= 3
          ? <Sparkline valores={dados.porSemana} />
          : (
            <Link href="/mercado/romaneios/novo" state={origemAtual()} className="text-xs font-semibold text-primary inline-flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" />Registrar romaneio em papel
            </Link>
          )}
      </div>
    </Tile>
  );
}

function TileComprado({ dados }: { dados: KpiComprado }) {
  const total = dados.porVariedade.PRATA + dados.porVariedade.NANICA;
  const pctPrata = total > 0 ? Math.round((dados.porVariedade.PRATA / total) * 100) : 0;
  return (
    <Tile rotulo={`Comprado · ${mesAtual()}`}>
      <p className="font-mono text-[clamp(18px,5.4vw,27px)] font-semibold tabular-nums leading-tight whitespace-nowrap" data-testid="kpi-comprado">
        {fmtToneladasSemUnidade(dados.kgMes)}<span className="ml-1.5 text-[13px] font-medium text-muted-foreground font-sans">toneladas</span>
      </p>
      <p className="text-xs text-muted-foreground">
        {dados.kgMes === 0
          ? "nenhum romaneio no mês"
          : dados.somenteRizoma
            ? "somente romaneios recebidos pelo Rizoma"
            : dados.caminhoesEquivalentes != null
              ? `≈ ${dados.caminhoesEquivalentes} ${dados.caminhoesEquivalentes === 1 ? "caminhão" : "caminhões"} de ${CAPACIDADE_REFERENCIA_KG / 1000} t`
              // Com kg no mês e menos de um caminhão, a frase — nunca "≈ 0
              // caminhões" logo abaixo do número que diz que ele comprou.
              : `menos de um caminhão de ${CAPACIDADE_REFERENCIA_KG / 1000} t`}
      </p>
      {total > 0 && (
        <div className="mt-auto hidden lg:block space-y-1">
          <div className="h-2 rounded-full bg-muted overflow-hidden flex">
            <div style={{ width: `${pctPrata}%`, background: "hsl(var(--chart-1))" }} />
            <div style={{ width: `${100 - pctPrata}%`, background: "hsl(var(--chart-2))" }} />
          </div>
          <p className="text-[11px] text-muted-foreground flex gap-3">
            <span>Prata {pctPrata}%</span>
            {dados.porVariedade.NANICA > 0 && <span>Nanica {100 - pctPrata}%</span>}
          </p>
        </div>
      )}
    </Tile>
  );
}

function TileAPagar({ dados }: { dados: KpiAPagar }) {
  const max = Math.max(...dados.porSemana, 1);
  const rotulos = ["esta sem.", "+1", "+2", "+3"];
  return (
    // ⚠️ LARGURA INTEIRA NO CELULAR. Dinheiro é o número mais longo do painel:
    // meia coluna de 390px dá 135px de conteúdo, e "R$ 113.644,40" precisa de
    // 165px — quebrava no meio do número. Encolher a fonte só adiava o problema
    // para o próximo dígito; a linha inteira resolve para qualquer valor.
    <Tile rotulo="A pagar aos produtores" className="col-span-2">
      <p className="font-mono text-[27px] font-semibold tabular-nums leading-tight whitespace-nowrap" data-testid="kpi-a-pagar">
        {fmtMoedaBR(dados.total)}
      </p>
      <p className="text-xs text-muted-foreground">
        {dados.nDocumentos === 1 && dados.proximoVencimento
          ? `1 romaneio · vence ${diaCurto(dados.proximoVencimento)}`
          : `${dados.nDocumentos} ${dados.nDocumentos === 1 ? "romaneio" : "romaneios"} · ${dados.nProdutores} ${dados.nProdutores === 1 ? "produtor" : "produtores"}`}
      </p>
      <div className="mt-auto hidden lg:block space-y-1">
        <div className="flex items-end gap-1.5 h-8">
          {dados.porSemana.map((v, i) => (
            <div key={i} className="flex-1 h-full flex items-end" title={`${rotulos[i]} · ${v > 0 ? fmtMoedaBR(v) : "nada vence"}`}>
              <div
                className={cn("w-full rounded-sm", v === 0 ? "bg-muted" : i === 0 ? "bg-atencao" : "bg-primary/30")}
                style={{ height: `${Math.max(4, (v / max) * 100)}%` }}
              />
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground flex justify-between">
          {rotulos.map(r => <span key={r}>{r}</span>)}
        </p>
      </div>
    </Tile>
  );
}

function Avatares({ itens }: { itens: { iniciais: string; noRizoma: boolean }[] }) {
  return (
    <span className="flex -space-x-1.5">
      {itens.map((a, i) => (
        <span
          key={`${a.iniciais}-${i}`}
          className={cn(
            "h-6 w-6 rounded-full grid place-items-center text-[10px] font-semibold ring-2 ring-card",
            a.noRizoma ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground",
          )}
        >
          {a.iniciais}
        </span>
      ))}
    </span>
  );
}

function TileProdutores({ dados }: { dados: KpiProdutores }) {
  return (
    // Largura inteira ao lado do "A pagar": meia linha sozinha deixava metade do
    // painel vazia logo acima da cotação.
    <Tile rotulo="Produtores parceiros" className="col-span-2 lg:col-span-1">
      <p className="font-mono text-[clamp(18px,5.4vw,27px)] font-semibold tabular-nums leading-tight whitespace-nowrap" data-testid="kpi-produtores">
        {dados.total}<span className="ml-1.5 text-[13px] font-medium text-muted-foreground font-sans">
          {dados.total === 1 ? "produtor" : "produtores"}
        </span>
      </p>
      <p className="text-xs text-muted-foreground">
        {dados.registroProprio > 0
          ? `${dados.noRizoma} no Rizoma · ${dados.registroProprio} com registro próprio`
          : "no Rizoma · envia os romaneios pela plataforma"}
      </p>
      <div className="mt-auto hidden lg:block">
        {dados.total > 1
          ? <Avatares itens={dados.avatares} />
          : (
            <Link href="/mercado/produtores" state={origemAtual()} className="text-xs font-semibold text-primary inline-flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" />Adicionar produtor
            </Link>
          )}
      </div>
    </Tile>
  );
}

// ─── Casca dos cards ─────────────────────────────────────────────────────────

function Card({ titulo, icone: Icone, meta, acao, children, testId }: {
  titulo: string;
  icone?: typeof Truck;
  meta?: React.ReactNode;
  acao?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid={testId}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          {Icone && <Icone className="h-4 w-4 text-muted-foreground" />}{titulo}
        </h3>
        {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
        {acao}
      </header>
      {children}
    </section>
  );
}

function Vazio({ icone: Icone, titulo, descricao, acao }: {
  icone: typeof Truck; titulo: string; descricao: string; acao?: React.ReactNode;
}) {
  return (
    <div className="px-4 py-8 text-center">
      <Icone className="h-6 w-6 mx-auto text-muted-foreground" />
      <p className="mt-2 text-sm font-medium">{titulo}</p>
      <p className="mt-1 text-xs text-muted-foreground max-w-[46ch] mx-auto">{descricao}</p>
      {acao && <div className="mt-3 flex flex-wrap justify-center gap-2">{acao}</div>}
    </div>
  );
}

// ─── §4.3 Cotação da praça ───────────────────────────────────────────────────

/**
 * O card é o MESMO de Cotações (`CardCotacaoDaPraca`) — sem "seu preço médio",
 * que o servidor não manda para o Início (decisão do #237).
 */
function Cotacao({ dados }: { dados: CotacaoPainelDTO }) {
  const [, setLocation] = useLocation();
  return (
    <CardCotacaoDaPraca
      dados={dados}
      titulo="Cotação da praça · hoje"
      comPracaNaMeta
      acao={
        <Button variant="ghost" size="sm" onClick={() => setLocation("/mercado/cotacoes")} data-testid="link-historico-cotacao">
          Histórico <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      }
    />
  );
}

// ─── §4.4 Cargas em andamento ────────────────────────────────────────────────

function Cargas({ dados }: { dados: PainelInicioDTO["cargas"] }) {
  // Antes do `if`: hook não pode nascer dentro de um ramo.
  const podeOperar = usePode("mercado.corretor.operar");
  if (dados.itens.length === 0) {
    return (
      <Card titulo="Cargas" icone={Truck} meta="nenhuma ainda" testId="card-cargas">
        <Vazio
          icone={Truck}
          titulo="Organize suas cargas"
          descricao="Uma carga reúne os romaneios de vários produtores que seguem no mesmo caminhão, recebidos pelo Rizoma ou de registro próprio. O sistema soma os kg e indica quanto falta para completar a capacidade."
          acao={
            podeOperar
              ? <Button asChild><Link href="/mercado/cargas" state={origemAtual()}>Criar primeira carga</Link></Button>
              : undefined
          }
        />
      </Card>
    );
  }

  return (
    <Card
      titulo="Cargas em andamento"
      icone={Truck}
      acao={
        <Button variant="ghost" size="sm" asChild>
          <Link href="/mercado/cargas" state={origemAtual()}>Ver todas <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
        </Button>
      }
      testId="card-cargas"
    >
      <div className="p-3 space-y-3">
        {dados.itens.map(c => <CardCarga key={c.id} carga={c} />)}
      </div>
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <span>
          Entregues em {mesAtual()}: {dados.entreguesNoMes.n} {dados.entreguesNoMes.n === 1 ? "carga" : "cargas"}
          {dados.entreguesNoMes.kg > 0 && <> · {fmtToneladas(dados.entreguesNoMes.kg)}</>}
        </span>
        {podeOperar && (
          <Button variant="outline" size="sm" asChild>
            <Link href="/mercado/cargas" state={origemAtual()}><Plus className="h-3.5 w-3.5 mr-1" />Nova carga</Link>
          </Button>
        )}
      </footer>
    </Card>
  );
}

function CardCarga({ carga }: { carga: CargaDoPainelDTO }) {
  const emFormacao = carga.status === "EM_FORMACAO";
  const pct = carga.alvoKg && carga.alvoKg > 0
    ? Math.min(100, Math.round((carga.kgLiquido / carga.alvoKg) * 100))
    : 100;

  return (
    <article className="rounded-lg border p-3 space-y-2" data-testid={`carga-${carga.identificador}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-semibold">{carga.identificador}</span>
        <StatusChip kind={emFormacao ? "info" : "conf"}>{emFormacao ? "Em formação" : "Fechada"}</StatusChip>
      </div>
      <p className="text-xs text-muted-foreground">
        {emFormacao
          ? <>carregamento {carga.dataCarregamento ? diaCurto(carga.dataCarregamento) : "a definir"} · {carga.destinoNome ?? "destino a definir"}</>
          : <>saiu {carga.dataCarregamento ? diaCurto(carga.dataCarregamento) : "—"} · {carga.destinoNome ?? "destino a definir"}{carga.placa && <> · placa {carga.placa}</>}</>}
      </p>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-mono text-lg font-semibold tabular-nums">
          {fmtKgBR(carga.kgLiquido)}
          <span className="text-xs font-normal text-muted-foreground ml-1">
            {carga.faltam != null && carga.faltam > 0 ? `de ${fmtKgBR(carga.alvoKg ?? 0)}` : "kg"}
          </span>
        </span>
        {carga.faltam != null && carga.faltam > 0
          ? <span className="text-xs font-semibold text-atencao">faltam {fmtKgBR(carga.faltam)}</span>
          : <span className="text-xs text-muted-foreground">composição fechada</span>}
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden border border-dashed">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "hsl(var(--chart-1))" }} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <Avatares itens={carga.produtores} />
          {carga.nProdutores} {carga.nProdutores === 1 ? "produtor" : "produtores"} · {carga.nRomaneios} {carga.nRomaneios === 1 ? "romaneio" : "romaneios"}
        </span>
        <span className="flex gap-2">
          {emFormacao && (
            <Button variant="outline" size="sm" asChild>
              <Link href={`/mercado/cargas/${carga.id}`} state={origemAtual()}>
                <MessageCircle className="h-3.5 w-3.5 mr-1" />Contatar produtores
              </Link>
            </Button>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link href={`/mercado/cargas/${carga.id}`} state={origemAtual()}>{emFormacao ? "Abrir carga" : "Abrir"}</Link>
          </Button>
        </span>
      </div>
    </article>
  );
}

// ─── §4.5 Romaneios recebidos ────────────────────────────────────────────────

const TOM_SITUACAO: Record<string, "aberta" | "warn" | "conf" | "info"> = {
  A_CONFERIR: "warn",
  CONFERIDO: "conf",
  AJUSTADO: "info",
  SEM_VENCIMENTO: "aberta",
  VENCE: "warn",
  CARGA: "info",
};

function SeloOrigem({ origem }: { origem: RomaneioRecente["origem"] }) {
  return origem === "RIZOMA"
    ? <StatusChip kind="conf">Rizoma</StatusChip>
    : <span className="inline-flex items-center rounded-full border border-dashed px-2 py-0.5 text-[11px] text-muted-foreground">Registro próprio</span>;
}

function Romaneios({ dados }: { dados: PainelInicioDTO["romaneios"] }) {
  const poucos = dados.recentes.length < 3;
  const podeOperar = usePode("mercado.corretor.operar");
  return (
    <Card
      titulo="Romaneios recebidos"
      icone={FileText}
      acao={
        <span className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/mercado/romaneios" state={origemAtual()}>Ver todos <ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
          {/* O MESMO destino do botão do cabeçalho — o rótulo tem de fazer a
              mesma coisa nos dois lugares (a regra que o #237 fixou). */}
          {podeOperar && (
            <Button size="sm" asChild data-testid="button-novo-romaneio-lista">
              <Link href="/mercado/romaneios/novo" state={origemAtual()}>
                <Plus className="h-3.5 w-3.5 mr-1" />Novo romaneio
              </Link>
            </Button>
          )}
        </span>
      }
      testId="card-romaneios"
    >
      {/* ⚠️ NO CELULAR NÃO EXISTE TABELA. Seis colunas em 390px davam 611px de
          conteúdo: a linha rolava de lado dentro do card, "Situação" ficava fora
          da tela e o corretor tinha de arrastar para ler o que mais importa. A
          MESMA informação vira cartão; a tabela volta a partir de `md`, onde ela
          é a melhor forma. */}
      <ul className="divide-y md:hidden">
        {dados.recentes.map(r => (
          <li key={r.ref} className="px-3 py-3 space-y-2" data-testid={`romaneio-${r.ref}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 min-w-0">
                  <b className="font-mono text-sm">{r.numero}</b>
                  <SeloOrigem origem={r.origem} />
                </p>
                <p className="text-sm mt-0.5 truncate">{r.produtorCurto}</p>
                <p className="text-[11px] text-muted-foreground">{diaCurto(r.data)} · {fmtKgBR(r.kg)}</p>
              </div>
              <p className="font-mono text-sm font-semibold tabular-nums whitespace-nowrap">{fmtMoedaBR(r.valor)}</p>
            </div>
            {r.situacoes.length > 0 && (
              <span className="flex flex-wrap gap-1">
                {r.situacoes.map(sit => (
                  <StatusChip key={sit.tipo} kind={TOM_SITUACAO[sit.tipo] ?? "aberta"}>{sit.rotulo}</StatusChip>
                ))}
              </span>
            )}
          </li>
        ))}
      </ul>

      {/* ⚠️ `overflow-x-auto` é REDE, não projeto: a tabela tem de CABER. Com seis
          colunas ela media 611px numa coluna de 570px e transbordava para fora
          do card — os chips de situação apareciam pintados no vão ao lado. A
          data saiu daqui (está no cartão do celular e no detalhe do romaneio), e
          o que sobra cabe. */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b">
              <th className="text-left font-semibold px-3 py-1.5">Romaneio</th>
              <th className="text-left font-semibold px-3 py-1.5">Produtor</th>
              <th className="text-right font-semibold px-3 py-1.5">kg</th>
              <th className="text-right font-semibold px-3 py-1.5">Valor</th>
            </tr>
          </thead>
          <tbody>
            {dados.recentes.map(r => (
              <tr key={r.ref} className="border-b last:border-0" data-testid={`romaneio-linha-${r.ref}`}>
                <td className="px-3 py-2">
                  <b className="font-mono">{r.numero}</b>
                  <span className="block text-[11px] text-muted-foreground whitespace-nowrap">{diaCurto(r.data)}</span>
                </td>
                {/* A situação mora AQUI, sob o nome, e não em coluna própria: com
                    seis colunas a tabela não cabia no card e o chip "Vence qui
                    24/09" era cortado na borda. Sob o nome ela tem a linha
                    inteira — e é a mesma leitura do cartão do celular. */}
                <td className="px-3 py-2">
                  <b className="block">{r.produtor}</b>
                  <span className="mt-1 flex flex-wrap items-center gap-1">
                    <SeloOrigem origem={r.origem} />
                    {r.situacoes.map(sit => (
                      <StatusChip key={sit.tipo} kind={TOM_SITUACAO[sit.tipo] ?? "aberta"}>{sit.rotulo}</StatusChip>
                    ))}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap">{fmtKgBR(r.kg)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums whitespace-nowrap align-top"><b>{fmtMoedaBR(r.valor)}</b></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Com menos de três romaneios a tabela ainda não é uma lista: a linha
          tracejada explica o que fazer, em vez de deixar um vão. */}
      {poucos && (
        <div className="m-3 rounded-lg border border-dashed p-3 flex flex-wrap items-center gap-3">
          <span className="min-w-0 flex-1 text-xs text-muted-foreground">
            <b className="block text-foreground text-sm">Romaneios em papel</b>
            Registre produtor, kg por classificação, preço e vencimento. O número do seu bloco pode ser
            usado como referência.
          </span>
          {podeOperar && (
            <Button size="sm" asChild data-testid="button-registrar-romaneio">
              <Link href="/mercado/romaneios/novo" state={origemAtual()}><Plus className="h-3.5 w-3.5 mr-1" />Registrar romaneio</Link>
            </Button>
          )}
        </div>
      )}

      {!poucos && (
        <footer className="border-t bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          {dados.rodape.mes} {dados.rodape.mes === 1 ? "romaneio" : "romaneios"} em {mesAtual()} ·{" "}
          {dados.rodape.viaRizoma} {dados.rodape.viaRizoma === 1 ? "recebido" : "recebidos"} pelo Rizoma ·{" "}
          {dados.rodape.registroProprio} de registro próprio
        </footer>
      )}
    </Card>
  );
}

// ─── §4.6 Pendências ─────────────────────────────────────────────────────────

const ICONE_PENDENCIA: Record<string, typeof Scale> = {
  CONFERIR: Scale,
  CARGA_FALTA: Truck,
  VENCE: CalendarClock,
  SEM_VENCIMENTO: CalendarClock,
  EXCEDENTE: Wallet,
  CONTESTADO: Wallet,
};

function Pendencias({ dados }: { dados: PainelInicioDTO["pendencias"] }) {
  const podeOperar = usePode("mercado.corretor.operar");
  const total = dados.itens.length + dados.aMais;

  return (
    <Card titulo="Pendências" meta={total > 0 ? `${total} ${total === 1 ? "item" : "itens"}` : undefined} testId="card-pendencias">
      {dados.itens.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhuma pendência. Tudo conferido e em dia.
        </p>
      ) : (
        <ul className="divide-y">
          {dados.itens.map((p, i) => <LinhaPendencia key={`${p.tipo}-${i}`} pendencia={p} podeOperar={podeOperar} />)}
        </ul>
      )}
      {dados.aMais > 0 && (
        <footer className="border-t bg-muted/40 px-3 py-2 text-xs">
          <Link href="/mercado/pagar" state={origemAtual()} className="font-semibold text-primary">
            e mais {dados.aMais} {dados.aMais === 1 ? "item" : "itens"}
          </Link>
        </footer>
      )}
    </Card>
  );
}

function LinhaPendencia({ pendencia, podeOperar }: { pendencia: PendenciaPainelDTO; podeOperar: boolean }) {
  const Icone = ICONE_PENDENCIA[pendencia.tipo] ?? CalendarClock;
  const alvo = pendencia.acoes.filter(a => a.tipo === "CONFERIR_OK")[0]?.alvo;
  const conferir = useConferirComprador(alvo);
  const { toast } = useToast();

  /**
   * "Confirmar kg" é a conferência do C3, chamada DAQUI — sem sair do painel.
   *
   * ⚠️ `bate: true` com `itens: []`: é o mesmo corpo que o link público manda
   * quando o comprador diz que confere. Quem valida e grava é
   * `registrarConferencia`; montar o cálculo aqui faria o painel e o link
   * divergirem no primeiro arredondamento.
   */
  function confirmar() {
    conferir.mutate({ bate: true, itens: [] } as never, {
      onSuccess: () => toast({ title: "Conferência registrada" }),
      onError: (e: unknown) => toast({
        title: "Não foi possível confirmar",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      }),
    });
  }

  return (
    <li className="px-3 py-3 flex gap-3" data-testid={`pendencia-${pendencia.tipo}`}>
      <span className={cn(
        "h-7 w-7 rounded-lg grid place-items-center shrink-0",
        pendencia.tom === "warn" ? "bg-atencao-suave text-atencao"
          : pendencia.tom === "info" ? "bg-info-suave text-info" : "bg-muted text-muted-foreground",
      )}>
        <Icone className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="text-sm font-semibold">{pendencia.titulo}</p>
        <p className="text-xs text-muted-foreground">{pendencia.sub}</p>
        {podeOperar ? (
          <span className="flex flex-wrap gap-2 pt-0.5">
            {pendencia.acoes.map((a, i) => {
              const primario = i === pendencia.acoes.length - 1;
              if (a.tipo === "CONFERIR_OK") {
                return (
                  <Button
                    key={a.tipo}
                    size="sm"
                    variant={primario ? "default" : "outline"}
                    disabled={conferir.isPending}
                    onClick={confirmar}
                    data-testid={`acao-${a.tipo}`}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" />{a.rotulo}
                  </Button>
                );
              }
              return (
                <Button key={a.tipo} size="sm" variant={primario ? "default" : "outline"} asChild data-testid={`acao-${a.tipo}`}>
                  <Link href={a.href ?? "/mercado/pagar"} state={origemAtual()}>{a.rotulo}</Link>
                </Button>
              );
            })}
          </span>
        ) : (
          // Quem só tem `ver` enxerga a pendência — ela é informação —, mas não
          // os botões: um botão que responde 403 ensina que o app está quebrado.
          <p className="text-[11px] text-muted-foreground italic">Peça a quem opera a conta para concluir</p>
        )}
      </div>
    </li>
  );
}

// ─── §4.7 Compras por semana ─────────────────────────────────────────────────

function ComprasPorSemana({ semanas }: { semanas: SemanaCompra[] }) {
  const comCompra = semanas.filter(s => s.kgPrata + s.kgNanica > 0).length;
  const totais = semanas.map(s => (s.kgPrata + s.kgNanica) / 1000);
  const teto = Math.max(20, Math.ceil(Math.max(...totais, 0) / 20) * 20);
  const fechadas = semanas.slice(0, 7);
  const media = fechadas.length > 0
    ? fechadas.reduce((a, s) => a + (s.kgPrata + s.kgNanica) / 1000, 0) / fechadas.length
    : 0;

  return (
    <Card titulo="Compras por semana" icone={TrendingUp} meta="toneladas · últimas 8 semanas" testId="card-compras-semana">
      <div className="relative px-3 py-3">
        {/* ⚠️ AS FONTES SÃO DO viewBox, não da tela. Em 390px o SVG desenha 520
            unidades em ~350px: `fontSize={10}` chega ao olho como 6,7px, que no
            celular não se lê. Os tamanhos abaixo são calibrados para isso. */}
        <svg viewBox="0 0 520 210" className="w-full" role="img" aria-label="Toneladas compradas por semana, Prata e Nanica">
          {[0, 20, 40, 60].filter(v => v <= teto).map(v => {
            const y = 18 + (1 - v / teto) * (210 - 18 - 34);
            return (
              <g key={v}>
                <line x1={34} x2={512} y1={y} y2={y} stroke="hsl(var(--border))" strokeWidth={1} />
                <text x={28} y={y + 4.5} textAnchor="end" fontSize={13} fill="hsl(var(--muted-foreground))">{v}</text>
              </g>
            );
          })}
          {semanas.map((s, i) => {
            const slot = (520 - 34 - 8) / semanas.length;
            const cx = 34 + slot * i + slot / 2;
            const bw = Math.min(34, slot * 0.56);
            const base = 18 + (210 - 18 - 34);
            const prata = (s.kgPrata / 1000);
            const nanica = (s.kgNanica / 1000);
            const yPrata = 18 + (1 - prata / teto) * (210 - 18 - 34);
            const yTopo = 18 + (1 - (prata + nanica) / teto) * (210 - 18 - 34);
            const rotulo = `${s.inicio.slice(8, 10)}/${s.inicio.slice(5, 7)}`;
            return (
              <g key={s.inicio} opacity={s.emCurso ? 0.55 : 1}>
                <title>{`semana de ${rotulo}${s.emCurso ? " (em curso)" : ""} · Prata ${prata.toFixed(1)} t${nanica > 0 ? ` · Nanica ${nanica.toFixed(1)} t` : ""} · total ${(prata + nanica).toFixed(1)} t`}</title>
                {prata + nanica === 0 ? (
                  <rect x={cx - bw / 2} y={base - 40} width={bw} height={40} rx={3}
                    fill="none" stroke="hsl(var(--border))" strokeDasharray="3 3" />
                ) : (
                  <>
                    <rect x={cx - bw / 2} y={yPrata} width={bw} height={Math.max(0, base - yPrata)} rx={3} fill="hsl(var(--chart-1))" />
                    {nanica > 0 && (
                      <rect x={cx - bw / 2} y={yTopo} width={bw} height={Math.max(0, yPrata - yTopo - 2)} rx={3} fill="hsl(var(--chart-2))" />
                    )}
                  </>
                )}
                <text x={cx} y={210 - 34 + 16} textAnchor="middle" fontSize={14} fill="hsl(var(--muted-foreground))">{rotulo}</text>
                {i >= semanas.length - 3 && prata + nanica > 0 && (
                  <text x={cx} y={yTopo - 6} textAnchor="middle" fontSize={14} className="font-mono" fill="hsl(var(--foreground))">
                    {(prata + nanica).toFixed(1)}
                  </text>
                )}
                {s.emCurso && (
                  <text x={cx} y={210 - 34 + 30} textAnchor="middle" fontSize={12} fill="hsl(var(--muted-foreground))">em curso</text>
                )}
              </g>
            );
          })}
        </svg>
        {comCompra < 2 && (
          <div className="absolute inset-0 grid place-items-center px-6">
            <p className="rounded-lg bg-card/95 border px-3 py-2 text-center text-xs text-muted-foreground max-w-[42ch]">
              Cada romaneio registrado entra neste gráfico, para você acompanhar a evolução do volume semanal.
            </p>
          </div>
        )}
      </div>
      <footer className="flex flex-wrap items-center gap-3 border-t px-3 py-2 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <i className="h-2.5 w-2.5 rounded-sm" style={{ background: "hsl(var(--chart-1))" }} />Prata
        </span>
        <span className="flex items-center gap-1.5">
          <i className="h-2.5 w-2.5 rounded-sm" style={{ background: "hsl(var(--chart-2))" }} />Nanica
        </span>
        {comCompra >= 2 && <span className="ml-auto">média 8 sem.: {media.toFixed(1)} t/sem</span>}
      </footer>
    </Card>
  );
}

// ─── §4.8 Produtores parceiros ───────────────────────────────────────────────

function Produtores({ dados }: { dados: PainelInicioDTO["produtores"] }) {
  const maior = Math.max(...dados.topo.map(p => p.kgMes), 1);
  return (
    <Card titulo="Produtores parceiros" icone={Users2} meta={`kg comprados em ${mesAtual()}`} testId="card-produtores">
      {dados.topo.length === 0 ? (
        <Vazio
          icone={Users2}
          titulo="Sua carteira está vazia"
          descricao="Produtores entram aqui sozinhos quando formalizam um romaneio para você — ou quando você registra a carga deles."
        />
      ) : (
        <ul className="divide-y">
          {dados.topo.map(p => <LinhaProdutor key={p.contatoId} produtor={p} maior={maior} />)}
        </ul>
      )}

      {dados.outros.n > 0 && (
        <p className="px-3 py-2 text-xs text-muted-foreground">
          mais {dados.outros.n} {dados.outros.n === 1 ? "produtor" : "produtores"} · {fmtToneladas(dados.outros.kg)}
        </p>
      )}

      {/* ⚠️ NÃO EXISTE "CONVIDAR" SEPARADO. O convite é o LINK do romaneio: ao
          registrar o papel do produtor, o link vai no WhatsApp e é por ele que
          o produtor cria a conta — o mesmo caminho pelo qual o comprador
          chegou. Um botão "Convidar" à parte prometia um segundo caminho que
          não existe. */}
      <div className="m-3 rounded-lg bg-sucesso-suave p-3 flex flex-wrap items-center gap-3">
        <span className="min-w-0 flex-1 text-xs">
          {dados.semRizoma === 0
            ? <><b>Cadastre os demais produtores.</b> Ao registrar o romaneio de cada um, o link do Rizoma vai junto — quem abrir cria a conta e passa a enviar pela plataforma.</>
            : <><b>{dados.semRizoma} {dados.semRizoma === 1 ? "produtor seu ainda não está" : "produtores seus ainda não estão"} no Rizoma.</b> O link do romaneio que você registra é o convite: quem abrir passa a enviar os romaneios prontos.</>}
        </span>
        <Button variant="outline" size="sm" asChild data-testid="button-produtores">
          <Link href="/mercado/produtores" state={origemAtual()}>
            <Users2 className="h-3.5 w-3.5 mr-1" />Ver produtores
          </Link>
        </Button>
      </div>
    </Card>
  );
}

function LinhaProdutor({ produtor, maior }: { produtor: ProdutorParceiro; maior: number }) {
  return (
    <li className="px-3 py-2.5 flex items-center gap-3" data-testid={`parceiro-${produtor.contatoId}`}>
      <Avatares itens={[{ iniciais: produtor.iniciais, noRizoma: produtor.noRizoma }]} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-sm font-medium flex items-center gap-1.5 min-w-0">
          <span className="truncate">{produtor.nome}</span>
          {produtor.noRizoma && <StatusChip kind="conf">Rizoma</StatusChip>}
        </p>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${(produtor.kgMes / maior) * 100}%`, background: "hsl(var(--chart-1))" }} />
        </div>
      </div>
      <p className="text-right text-xs whitespace-nowrap">
        <b className="font-mono tabular-nums">{fmtToneladas(produtor.kgMes)}</b>
        <span className="block text-muted-foreground">
          {produtor.romaneiosMes} {produtor.romaneiosMes === 1 ? "romaneio" : "romaneios"}
        </span>
      </p>
    </li>
  );
}
